import React, { useState } from 'react'
import { X, Calendar, Users, AlertCircle } from 'lucide-react'

interface CreatePayrollRunModalProps {
  onClose: () => void
  onPayrollCreated: () => void
}

const CreatePayrollRunModal: React.FC<CreatePayrollRunModalProps> = ({ onClose, onPayrollCreated }) => {
  const [formData, setFormData] = useState({
    periodStart: '',
    periodEnd: '',
    description: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('')
  }

  const validateForm = () => {
    if (!formData.periodStart) {
      setError('Pay period start date is required')
      return false
    }
    if (!formData.periodEnd) {
      setError('Pay period end date is required')
      return false
    }
    if (new Date(formData.periodStart) >= new Date(formData.periodEnd)) {
      setError('Pay period end date must be after start date')
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setLoading(true)
    setError('')

    try {
      const token = localStorage.getItem('accessToken')
      const response = await fetch('/api/v1/payroll/runs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          periodStart: formData.periodStart,
          periodEnd: formData.periodEnd,
          description: formData.description || undefined
        })
      })

      let result: any = null
        try {
          result = await response.json()
        } catch {
          result = {}
        }

        if (response.ok) {
          onPayrollCreated()
        } else {
          setError(result?.message || `Server error (${response.status})`)
        }

    } catch (error) {
      console.error('Error creating payroll run:', error)
      setError('Failed to create payroll run. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const getDefaultPayPeriod = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth()
    
    // Previous month
    const start = new Date(year, month - 1, 1)
    const end = new Date(year, month, 0) // Last day of previous month
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    }
  }

  const defaultPeriod = getDefaultPayPeriod()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Calendar className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Create Payroll Run</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {/* Pay Period Start */}
          <div>
            <label htmlFor="periodStart" className="block text-sm font-medium text-gray-700 mb-1">
              Pay Period Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="periodStart"
              name="periodStart"
              value={formData.periodStart || defaultPeriod.start}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Pay Period End */}
          <div>
            <label htmlFor="periodEnd" className="block text-sm font-medium text-gray-700 mb-1">
              Pay Period End Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="periodEnd"
              name="periodEnd"
              value={formData.periodEnd || defaultPeriod.end}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="e.g., Monthly payroll for October 2024"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              If left blank, a description will be generated automatically
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <Users className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">What happens next?</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>All active employees will be processed</li>
                  <li>Salaries, taxes, and deductions will be calculated</li>
                  <li>Payslips will be generated automatically</li>
                  <li>You can review and approve the payroll run when complete</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {loading ? 'Creating...' : 'Create Payroll Run'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreatePayrollRunModal